import {useDispatch, useSelector} from "react-redux";
import {usersActions} from "@features/users";
import {components, StepType, TourProvider, useTour} from "@reactour/tour";
import {DependencyList, useEffect} from "react";
import {currentUserSelectors} from "@features/currentUser";
import {App, Button} from "antd";
import {useTranslation} from "react-i18next";
import {getLocalStorage, setLocalStorage} from "@sfs/utils/localStorageUtilities";

/**
 * @param {string} tabKey
 */
export const clickOnTab = (tabKey) => {
  clickOnElement(`[data-node-key='${tabKey}']`);
};

/**
 * @param {string} selector
 */
export const clickOnElement = (selector) => {
  try {
    document.querySelector(selector).click();
  } catch (e) {
    console.log("failed to click on element in user tour.");
  }
};

/**
 * @param {string} tabKey
 * @param {string} [selector]
 */
export const selectTab = (tabKey, selector) => ({
  selector: selector || `[data-node-key='${tabKey}']`,
  resizeObservables: selector ? [selector] : [`[data-node-key='${tabKey}']`],
  mutationObservables: selector ? [selector] : [`[data-node-key='${tabKey}']`],
  action: () => clickOnTab(tabKey),
});

const toggleSidebar = () => {
  try {
    // Open the sidebar if it exists
    document.querySelector(".navbar-menu-button > *").click();
  } catch (e) {
    // do nothing if it fails
  }
};
export const inSidebar = (selector, {actionAfter: extraActionAfter} = {}) => ({
  action: toggleSidebar,
  actionAfter: () => {
    toggleSidebar();
    extraActionAfter?.();
  },
  selector, // Resize if the sidebar is collapsed from the agenda mode
  resizeObservables: [selector],
});

const ONGOING_TOUR_KEY = "ongoing-tour";

/**
 * @param {string} tourKey
 * @param {Array<StepType>} steps
 * @param {object} [options]
 * @param {number} [options.delayBeforeShow]
 * @param {function(currentUser: object): boolean} [options.shouldShowNow]
 * @param {DependencyList} [options.deps]
 */
export const useUserTour = (
  tourKey,
  steps,
  {delayBeforeShow = 500, shouldShowNow = () => true, deps = []} = {}
) => {
  const {setSteps, setCurrentStep, setIsOpen, setMeta, isOpen} = useTour();

  const authenticatedUser = useSelector(currentUserSelectors.selectAuthenticatedUser);
  const shouldShowTour = !authenticatedUser.shownTours?.includes(tourKey);

  useEffect(() => {
    if (shouldShowTour && !isOpen && shouldShowNow(authenticatedUser)) {
      const timeoutId = setTimeout(() => {
        setLocalStorage(ONGOING_TOUR_KEY, tourKey);
        setMeta(tourKey);
        setCurrentStep(0);
        setSteps(steps);
        setIsOpen(true);
      }, delayBeforeShow);
      return () => {
        clearTimeout(timeoutId);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowTour, setCurrentStep, setSteps, isOpen, ...deps]);
};

export const UserTourProvider = ({children}) => {
  const {message} = App.useApp();
  const dispatch = useDispatch();
  const {t} = useTranslation();

  const authenticatedUser = useSelector(currentUserSelectors.selectAuthenticatedUser);

  // On close tour, add the tour to the shown tours, so it is not shown again to the user
  const closeAndDontDisplayAnymore = ({currentStep, steps, setIsOpen}) => {
    setIsOpen(false);

    const tourKey = getLocalStorage(ONGOING_TOUR_KEY);
    if (tourKey === "" || authenticatedUser?.shownTours?.includes(tourKey)) return;
    dispatch(
      usersActions.persist(
        {
          _id: authenticatedUser._id,
          shownTours: [...(authenticatedUser.shownTours || []), tourKey],
        },
        false
      )
    );
    if (currentStep < steps.length - 1) {
      message.info(
        `Vous n\'avez pas terminé le tutoriel. Pour le revoir, allez dans votre profil et cliquez sur "${t(
          "users:schema.resetUserTours.buttonTitle"
        )}".`,
        6
      );
    }

    localStorage.removeItem(ONGOING_TOUR_KEY);
  };

  return (
    <TourProvider
      scrollSmooth
      steps={[]}
      onClickClose={closeAndDontDisplayAnymore}
      keyboardHandler={(e, clickProps) => {
        // Keep default behavior for arrows
        if (e.key === "ArrowRight") {
          if (clickProps.currentStep === clickProps.steps.length - 1) {
            closeAndDontDisplayAnymore(clickProps); // If last step, pressing the key closes the tour
          } else {
            clickProps.setCurrentStep(
              Math.min(clickProps.currentStep + 1, clickProps.steps.length - 1)
            );
          }
        }
        if (e.key === "ArrowLeft") {
          clickProps.setCurrentStep(Math.max(clickProps.currentStep - 1, 0));
        }

        // On escape, don't display anymore
        if (e.key === "Escape") {
          closeAndDontDisplayAnymore(clickProps);
        }
      }}
      onClickMask={closeAndDontDisplayAnymore}
      components={{
        Navigation: (props) => {
          const {currentStep, steps} = props;
          return (
            steps.length > 1 && (
              <components.Navigation
                {...props}
                nextButton={
                  currentStep === steps.length - 1
                    ? (props) => (
                        <Button type={"primary"} onClick={() => closeAndDontDisplayAnymore(props)}>
                          {t("common:ok")}
                        </Button>
                      )
                    : props.nextButton
                }
                prevButton={
                  currentStep === 0 ? () => <div style={{width: 16}}></div> : props.prevButton
                }
              />
            )
          );
        },
      }}
      styles={{
        popover: (base) => ({
          ...base,
          "--reactour-accent": "var(--noe-accent)",
          background: "var(--colorBgElevated)",
          borderRadius: 10,
        }),
        close: (base) => ({...base, right: 10, top: 10, transform: "scale(1.4)"}),
        arrow: (base) => ({...base, transform: "scale(1.6)"}),
        dot: (base) => ({...base, transform: "scale(1.2)"}),
        badge: (base) => ({...base, background: "var(--noe-gradient)"}),
        maskWrapper: (base) => ({...base, color: "var(--text)", opacity: 0.4}),
        maskArea: (base) => ({...base, rx: 14}),
      }}>
      {children}
    </TourProvider>
  );
};
